import React from 'react';
import PropTypes from 'prop-types';

import { isPitchOrigin } from 'views_shared/app/javascript/constants/conversationOriginTypes';
import { qformat, DATE_FORMATS } from 'views_shared/app/javascript/utils/dateFormatters';

import Message from '../Message';
import { useMessagesActions, useMessagesState } from '../MessagesContext';
import PitchAsMessage from '../PitchAsMessage';
import DateSeparator from './DateSeparator';
import MessagesSkeleton from './MessagesSkeleton';
import useAutoScroll from './useAutoScroll';

const needsSeparator = (currentMsg, previousMsg) => {
  if (!previousMsg) return true;
  return qformat(currentMsg.sent_at, DATE_FORMATS.isoDate) !== qformat(previousMsg.sent_at, DATE_FORMATS.isoDate);
};

// pitchBanner: optional slot forwarded to PitchAsMessage (pitch inbox's flagged-user
// warning); null (the default) keeps the messaging inbox exactly as before.
function Messages({ conversation, pitchBanner = null }) {
  const {
    messages, pendingScrollRef, isLoading, isLoadingOlder, hasMoreOlder,
  } = useMessagesState();
  const { onMarkRead, loadOlder } = useMessagesActions();

  const {
    scrollContainerRef,
    contentRef,
    messagesEndRef,
    topSentinelRef,
    hasNewBelow,
    scrollPending,
    scrollToBottom,
    handleScroll,
  } = useAutoScroll(messages, conversation?.conversation_id, pendingScrollRef, onMarkRead, { hasMoreOlder, loadOlder });

  const showSkeleton = isLoading && messages.length === 0;

  return (
    <div
      ref={scrollContainerRef}
      onScroll={handleScroll}
      className="d-flex flex-column flex-grow-1 overflow-y-auto overflow-x-hidden position-relative"
      style={{ overflowAnchor: 'none', ...(scrollPending ? { visibility: 'hidden', overflow: 'hidden' } : {}) }}
    >
      {isLoadingOlder && (
        <div className="position-absolute top-0 start-50 translate-middle-x mt-2" style={{ zIndex: 1 }}>
          <span
            className="spinner-border spinner-border-sm text-secondary"
            role="status"
            aria-label="Loading older messages"
          />
        </div>
      )}

      <div ref={contentRef} className="d-flex flex-column flex-shrink-0">
        {/* Sentinel for older-message infinite scroll — must stay the first child */}
        <span ref={topSentinelRef} />

        {showSkeleton ? (
          <MessagesSkeleton />
        ) : (
          <>
            {/* The pitch precedes every message — show it only once the oldest page is loaded */}
            {isPitchOrigin(conversation?.origin_type) && !hasMoreOlder && (
              <PitchAsMessage conversation={conversation} banner={pitchBanner} />
            )}

            {conversation
              && messages.map((message, index) => (
                <React.Fragment key={message.id}>
                  {needsSeparator(message, messages[index - 1]) && <DateSeparator date={message.sent_at} />}

                  <Message message={message} conversation={conversation} />
                </React.Fragment>
              ))}
          </>
        )}

        <span ref={messagesEndRef} />
      </div>

      {hasNewBelow && (
        <button
          type="button"
          onClick={scrollToBottom}
          className="btn btn-primary btn-sm rounded-pill shadow position-sticky mx-auto d-block"
          style={{ bottom: 8 }}
        >
          New message <i className="fa-solid fa-arrow-down ms-1" />
        </button>
      )}
    </div>
  );
}

const profileShape = PropTypes.shape({
  slug: PropTypes.string,
  name: PropTypes.string,
});

Messages.propTypes = {
  conversation: PropTypes.shape({
    conversation_id: PropTypes.string.isRequired,
    origin_type: PropTypes.string,
    conversation_senders: PropTypes.arrayOf(profileShape),
    conversation_receivers: PropTypes.arrayOf(profileShape),
  }),
  pitchBanner: PropTypes.node,
};

export default Messages;
